import { useEffect, type ReactNode } from 'react';
import { track } from '@/lib/analytics';
import {
  ANSWER_DIFFICULTIES,
  type AnswerDifficulty,
} from '../../domain/entities/answer-key.entity';
import type { TendenciaReading } from '../../domain/entities/tendencia.entity';

/** Concordância em número: "1 questões" é o erro que todo mundo nota. */
function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/**
 * O diagnóstico em si: a categoria, o total de acertos, o texto da categoria e
 * o aproveitamento por dificuldade. É a `Reading` da `TendenciaSection` do
 * `enamed-feedback-lp`, com as menções ao ENAMED e à nota oficial trocadas pela
 * prova do tenant.
 */
export function DiagnosticoReading({
  reading,
  examName,
  subject,
}: {
  reading: TendenciaReading;
  examName: string;
  /** Sobre o que é a leitura: o ensaio, ou o gabarito da prova real. */
  subject: 'ensaio' | 'prova';
}) {
  const { label, tone } = badgeOf(reading);

  // A categoria não é dado de pessoa (é uma de quatro), e diz ao marketing que
  // tipo de recado cada um recebeu — o que sustenta uma campanha de conteúdo.
  useEffect(() => {
    track({
      event: 'diagnostico_visualizado',
      fonte: subject,
      categoria: reading.category,
    });
  }, [subject, reading.category]);

  return (
    <div className="stack-lg">
      <section className="card result-card">
        <p className={`tag result-badge is-${tone}`}>{label}</p>

        {/* Fração, e não um número solto: "14 / 20" não se confunde com nota. */}
        <p className="figure-label">
          Seu total de <span className="figure-label-accent">acertos</span>
        </p>
        <p className="figure figure-sm">
          {reading.acertos}
          <span className="figure-of">/{reading.questoesValidas}</span>
        </p>

        <p className="result-text">{describe(reading)}</p>

        <p className="note result-warning">
          {subject === 'ensaio'
            ? 'Este é um diagnóstico do seu desempenho no ensaio e '
            : 'Este é um diagnóstico do padrão das suas respostas e '}
          <span className="result-warning-strong">
            não representa a sua <strong>nota</strong>{' '}
            {subject === 'prova' ? 'oficial ' : ''}na {examName}.
          </span>
        </p>

        {reading.naoClassificadas > 0 && (
          <p className="result-footnote">
            {plural(
              reading.naoClassificadas,
              'questão ainda não tem',
              'questões ainda não têm',
            )}{' '}
            dificuldade classificada e ficaram de fora do diagnóstico.
          </p>
        )}
      </section>

      <DifficultyBreakdown reading={reading} />
    </div>
  );
}

const DIFFICULTY_LABEL: Record<AnswerDifficulty, string> = {
  EASY: 'Fáceis',
  MEDIUM: 'Médias',
  HARD: 'Difíceis',
};

/**
 * O aproveitamento faixa a faixa, em barra de três segmentos — acerto, erro e
 * branco —, como no ENAMED. A barra mede a faixa inteira, branco incluído,
 * para a abstenção não sumir do denominador.
 */
function DifficultyBreakdown({ reading }: { reading: TendenciaReading }) {
  const faixas = ANSWER_DIFFICULTIES.map((difficulty) => {
    const { respondidas, acertos, emBranco } =
      reading.porDificuldade[difficulty];
    return {
      difficulty,
      acertos,
      erros: respondidas - acertos,
      emBranco,
      total: respondidas + emBranco,
    };
  }).filter((faixa) => faixa.total > 0);

  if (faixas.length === 0) return null;

  return (
    <section className="card">
      <h3>Aproveitamento por dificuldade</h3>
      <ul className="legend">
        <li>
          <span className="bar-swatch is-acerto" aria-hidden="true" />
          Acertos
        </li>
        <li>
          <span className="bar-swatch is-erro" aria-hidden="true" />
          Erros
        </li>
        <li>
          <span className="bar-swatch is-branco" aria-hidden="true" />
          Em branco
        </li>
      </ul>

      <div className="stack-md">
        {faixas.map(({ difficulty, acertos, erros, emBranco, total }) => (
          <div key={difficulty}>
            <div className="meter-head">
              <span className="meter-label">
                {DIFFICULTY_LABEL[difficulty]}
              </span>
              <span className="meter-value">
                {acertos} de {total}
                <span className="meter-percent">
                  {Math.round((acertos / total) * 100)}%
                </span>
              </span>
            </div>
            {/* Erro e branco só existem no desenho, então a barra tem rótulo
                por extenso para quem não a enxerga. */}
            <div
              className="meter"
              role="img"
              aria-label={`${DIFFICULTY_LABEL[difficulty]}: ${plural(acertos, 'acerto', 'acertos')}, ${plural(erros, 'erro', 'erros')}${
                emBranco > 0 ? `, ${emBranco} em branco` : ''
              }.`}
            >
              <span
                className="meter-part is-acerto"
                style={{ width: `${(acertos / total) * 100}%` }}
              />
              <span
                className="meter-part is-erro"
                style={{ width: `${(erros / total) * 100}%` }}
              />
              <span
                className="meter-part is-branco"
                style={{ width: `${(emBranco / total) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * Rótulo e cor de cada leitura. As três categorias formam uma escala; os dois
 * motivos de "sem leitura" ficam fora dela, em cinza. O rótulo diz o mesmo que
 * a cor, porque cor sozinha não chega a quem não a enxerga.
 */
function badgeOf(reading: TendenciaReading): { label: string; tone: string } {
  switch (reading.category) {
    case 'ERROS_NAS_DIFICEIS':
      return { label: 'Padrão favorável', tone: 'favoravel' };
    case 'DISTRIBUICAO_ESPERADA':
      return { label: 'Padrão neutro', tone: 'neutro' };
    case 'PERDA_NAS_FACEIS':
      return { label: 'Padrão desfavorável', tone: 'desfavoravel' };
    case 'SEM_LEITURA':
      return reading.reason === 'AMOSTRA'
        ? { label: 'Ainda não dá para ler seu padrão', tone: 'sem-leitura' }
        : {
            label: 'Seu caso pede uma leitura questão a questão',
            tone: 'sem-leitura',
          };
  }
}

/**
 * O texto de cada categoria — o do ENAMED ("Casos de Leitura de Padrão — copy
 * literal"), tirando o que só vale para lá: a frase "No ENAMED, …" e o "não
 * desconta ponto por chute", que é regra daquela prova.
 */
function describe(reading: TendenciaReading): ReactNode {
  const { naoLembro, category, reason } = reading;

  if (category === 'SEM_LEITURA') {
    return reason === 'AMOSTRA' ? (
      <>
        Você deixou{' '}
        {plural(naoLembro, 'questão em branco', 'questões em branco')}. Com
        tantas respostas ausentes, não dá para ler seu padrão com confiança.
        Vale para a próxima prova: em branco <strong>conta como erro</strong>.
      </>
    ) : (
      <>
        <strong>Comparar as questões aqui não muda o resultado final.</strong>{' '}
        Quase todo mundo nesta faixa de acertos erra independente do nível de
        dificuldade. Seu caso pede uma leitura questão a questão.
      </>
    );
  }

  if (category === 'ERROS_NAS_DIFICEIS') {
    return (
      <>
        <strong>Seus erros se concentraram nas questões mais difíceis.</strong>{' '}
        Os acertos em questões de dificuldade menor foram consistentes. O que
        separa um candidato como você dos que vão conseguir as melhores vagas é
        a revisão dos seus erros frequentes para aumentar a profundidade de
        conteúdo. Normalmente os seus erros já vem de temas que você sabe que
        tem maior dificuldade.
      </>
    );
  }

  if (category === 'DISTRIBUICAO_ESPERADA') {
    return (
      <>
        <strong>Você se manteve em equilíbrio.</strong> Os erros se distribuem e
        não há um ponto único de atenção. Para aumentar, o caminho é manter um
        hábito de treino e repetição áreas e temas que você tem menos
        aproveitamento e ainda deixou passar questões de dificuldade fácil e
        média.
      </>
    );
  }

  return (
    <>
      <strong>Você deixou escapar questões mais acessíveis.</strong> Você errou
      mais questões do que o que era esperado para o seu total de acertos. Eram
      pontos que estavam ao seu alcance, e o erro em uma questão como essa
      costuma ser visto como um erro por pressa, falha de leitura ou desatenção.
      É o erro mais barato de corrigir em provas, aqueles que os melhores
      candidatos não deixam passar.
    </>
  );
}
